import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, Modal, SectionIntro, formatDateTime, errorMessage } from '../components/ui';
import './Templates.css';

/**
 * Insert a dynamic field at the cursor.
 *
 * A dropdown rather than a wall of chips: there are sixteen fields, and an
 * author wants the one they are thinking of, grouped by what it is about.
 * Each row shows an example value, which is usually all the explanation the
 * name needs.
 */
function FieldPicker({ tokens, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const escape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const groups = tokens.reduce((acc, t) => {
    const key = t.group || 'Fields';
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});

  return (
    <span className="tmpl-picker" ref={ref}>
      <button type="button" className="tmpl-picker-btn" onClick={() => setOpen(!open)}>
        + Insert field
      </button>
      {open && (
        <div className="tmpl-picker-menu">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <span className="tmpl-picker-group">{group}</span>
              {items.map((t) => (
                <button
                  type="button"
                  key={t.token}
                  className="tmpl-picker-item"
                  onClick={() => { onPick(t.token); setOpen(false); }}
                  title={t.description}
                >
                  <code>{`{{${t.token}}}`}</code>
                  {t.sample && <span className="tmpl-picker-sample">{t.sample}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * The blank template rendered to PDF, shown as a thumbnail.
 *
 * Fetched rather than pointed at directly: the endpoint needs a bearer token
 * and an <iframe> cannot send one, so the bytes come back through fetch and
 * become an object URL. First render converts the .docx server-side and can
 * take a few seconds; after that it is cached.
 */
function TemplatePreview({ templateKey, name, onOpen }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/api/templates/documents/${templateKey}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || 'Could not render this template.');
        }
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [templateKey]);

  if (error) {
    return <div className="tmpl-preview tmpl-preview-failed"><span>{error}</span></div>;
  }

  if (!url) {
    return <div className="tmpl-preview tmpl-preview-loading"><span>Rendering preview…</span></div>;
  }

  return (
    <button type="button" className="tmpl-preview" onClick={() => onOpen(url)}
      title={`Open ${name} full size`}>
      {/* Inert on purpose: the iframe is a thumbnail, and the button around it
          takes the click. #view=Fit sizes the page to the frame. */}
      <iframe src={`${url}#view=Fit&toolbar=0&navpanes=0`} title={`${name} preview`} tabIndex={-1} />
      <span className="tmpl-preview-open">Click to enlarge</span>
    </button>
  );
}

/**
 * What this template fills in, and what it could.
 *
 * Three states matter to whoever edits the Word file: a field the template
 * uses, a field it could use but doesn't, and a tag in the document that the
 * app does not recognise. The last is the one worth catching — a mistyped tag
 * looks perfectly fine in Word and comes out blank on a client's certificate.
 */
function FieldRow({ field, detailed }) {
  return (
    <li className="tmpl-field">
      <code>{field.tag}</code>
      <span className="tmpl-field-label">{field.label}</span>
      {detailed && (
        <span className="tmpl-field-detail">
          {field.description}
          {field.aliasOf && <> Carries the same value as <code>{field.aliasOf}</code>.</>}
          <span className="tmpl-field-source">From: {field.source}</span>
        </span>
      )}
    </li>
  );
}

function FieldTable({ template }) {
  const [showUnused, setShowUnused] = useState(false);
  const [detailed, setDetailed] = useState(false);

  const fields = template.fields || [];
  const used = fields.filter((f) => f.inTemplate);
  const unused = fields.filter((f) => !f.inTemplate);
  const unrecognised = template.unrecognised || [];

  if (!template.fileExists) {
    return <p className="tmpl-none">The file is missing, so its fields cannot be read.</p>;
  }

  return (
    <div className="tmpl-fields">
      <span className="tmpl-controls-label">
        Fields this template fills
        <span className="tmpl-count">{used.length} of {fields.length}</span>
      </span>

      {used.length === 0 ? (
        <span className="tmpl-none">
          No recognised fields — nothing would be populated in this document.
        </span>
      ) : (
        <ul className={`tmpl-field-list${detailed ? ' is-detailed' : ''}`}>
          {used.map((f) => <FieldRow key={f.tag} field={f} detailed={detailed} />)}
        </ul>
      )}

      {unrecognised.length > 0 && (
        <div className="callout callout-warning tmpl-unknown">
          <span className="callout-label">Not recognised — these stay blank</span>
          <div className="tmpl-tags">
            {unrecognised.map((u) => <span className="tmpl-tag is-bad" key={u.tag}>{u.tag}</span>)}
          </div>
          <span className="tmpl-unknown-hint">
            Nothing in the app fills these — the tag is either misspelled or no longer needed.
          </span>
        </div>
      )}

      <div className="tmpl-toggles">
        {used.length > 0 && (
          <button type="button" className="tmpl-toggle" onClick={() => setDetailed(!detailed)}>
            {detailed ? 'Hide' : 'What each field holds'}
          </button>
        )}
        {unused.length > 0 && (
          <button type="button" className="tmpl-toggle" onClick={() => setShowUnused(!showUnused)}>
            {showUnused ? 'Hide available' : `${unused.length} more available`}
          </button>
        )}
      </div>

      {showUnused && (
        <ul className={`tmpl-field-list is-muted${detailed ? ' is-detailed' : ''}`}>
          {unused.map((f) => <FieldRow key={f.tag} field={f} detailed={detailed} />)}
        </ul>
      )}
    </div>
  );
}

function Templates() {
  const [docs, setDocs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState('');

  const [editingEmail, setEditingEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', bodyHtml: '' });
  const [uploadResult, setUploadResult] = useState(null);

  // The enlarged preview. The URL belongs to the card's TemplatePreview, which
  // owns and revokes it, so this only borrows it while the overlay is open.
  const [viewing, setViewing] = useState(null);

  // The email editor: a live render with sample values, so an author sees the
  // message a client would get rather than a page of {{braces}}.
  const bodyRef = useRef(null);
  const [rendered, setRendered] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, e, t] = await Promise.all([
        axios.get(`${API_BASE}/api/templates/documents`),
        axios.get(`${API_BASE}/api/templates/emails`),
        axios.get(`${API_BASE}/api/templates/emails/tokens`),
      ]);
      setDocs(d.data);
      setEmails(e.data);
      setTokens(t.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load templates.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rendered on the server, through the same function a real send uses, so the
  // preview cannot quietly disagree with what the client receives. Debounced
  // because it fires on every keystroke.
  useEffect(() => {
    if (!editingEmail) { setRendered(null); return undefined; }

    const timer = setTimeout(() => {
      axios.post(`${API_BASE}/api/templates/emails/preview`, emailForm)
        .then((r) => setRendered(r.data))
        .catch(() => setRendered(null));
    }, 350);

    return () => clearTimeout(timer);
  }, [editingEmail, emailForm]);

  const upload = async (key, file) => {
    if (!file) return;
    setBusy(key);
    setError('');
    setSuccess('');
    setUploadResult(null);
    try {
      const data = new FormData();
      data.append('template', file);
      const res = await axios.post(`${API_BASE}/api/templates/documents/${key}/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult({
        key,
        controls: res.data.contentControls,
        matched: res.data.matched || [],
        unrecognised: res.data.unrecognised || [],
      });
      setSuccess('Template replaced.');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
      if (fileInputs.current[key]) fileInputs.current[key].value = '';
    }
  };

  const setDateMode = async (key, dateMode) => {
    setError('');
    try {
      await axios.patch(`${API_BASE}/api/templates/documents/${key}`, { dateMode });
      setSuccess('Date mode updated.');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const downloadTemplate = (key, fileName) => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/templates/documents/${key}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Download failed.');
        return res.blob();
      })
      .then((blob) => {
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(href);
      })
      .catch((err) => setError(err.message));
  };

  const openEmail = (t) => {
    setEmailForm({ subject: t.Subject, bodyHtml: t.BodyHtml });
    setEditingEmail(t);
  };

  /**
   * Edit the body around whatever the author has selected, then put the caret
   * back where they would expect it. Without the restore, every toolbar click
   * would dump the cursor at the end of the document.
   */
  const editBody = (fn) => {
    const el = bodyRef.current;
    if (!el) return;

    const { selectionStart: start, selectionEnd: end } = el;
    const body = emailForm.bodyHtml;
    const { text, caret } = fn(body.slice(start, end));
    const next = body.slice(0, start) + text + body.slice(end);

    setEmailForm((prev) => ({ ...prev, bodyHtml: next }));

    const at = start + (caret === undefined ? text.length : caret);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at, at);
    });
  };

  // Wraps a selection, or drops the tags in ready to type between when there
  // is no selection.
  const wrap = (open, close) =>
    editBody((sel) => ({ text: `${open}${sel}${close}`, caret: open.length + sel.length }));

  const insert = (snippet) => editBody(() => ({ text: snippet }));

  const saveEmail = async () => {
    setBusy('email');
    setError('');
    try {
      await axios.put(`${API_BASE}/api/templates/emails/${editingEmail.Key}`, emailForm);
      setSuccess(`"${editingEmail.Name}" saved.`);
      setEditingEmail(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="page-container"><div className="page-card"><Loading /></div></div>;
  }

  return (
    <div className="page-container">
      {/* ---------------- Word templates ---------------- */}
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Word templates</h2>
            <p className="page-subtitle">
              The documents generated for each outcome. Values are written into the Word content
              controls (developer fields), so a replacement template keeps working as long as it
              carries the same field tags.
            </p>
          </div>
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <SectionIntro>
          Each card previews the blank document and lists the fields it fills, by the exact tag
          name to put on the Word content control — tag a control with one of these and it is
          populated, tag it with anything else and it stays blank. Certificates carry the validity
          range; letters normally carry the day they were issued. A replacement upload is checked
          first: if it cannot be read as Word, or has no content controls, the previous template is
          restored and nothing changes.
        </SectionIntro>

        <div className="tmpl-grid">
          {docs.map((d) => (
            <div className="tmpl-card" key={d.Key}>
              <div className="tmpl-card-head">
                <div>
                  <h3>{d.Name}</h3>
                  <span className="tmpl-kind badge badge-grey">{d.Kind}</span>
                  {!d.fileExists && <span className="badge badge-red tmpl-kind">File missing</span>}
                </div>
              </div>

              <div className="tmpl-meta">
                <span className="mono">{d.FileName}</span>
                {d.sizeBytes != null && <span> · {(d.sizeBytes / 1024).toFixed(0)} KB</span>}
                {d.UploadedAt && <span> · updated {formatDateTime(d.UploadedAt)}</span>}
              </div>

              {d.fileExists && (
                <TemplatePreview
                  templateKey={d.Key}
                  name={d.Name}
                  onOpen={(url) => setViewing({ url, name: d.Name })}
                />
              )}

              <FieldTable template={d} />

              <div className="field tmpl-datemode">
                <label htmlFor={`dm-${d.Key}`}>Issue date written in</label>
                <select id={`dm-${d.Key}`} value={d.DateMode} onChange={(e) => setDateMode(d.Key, e.target.value)}>
                  <option value="range">Validity range — 11/08/2026 - 11/02/2027</option>
                  <option value="single">Single date — 11/08/2026</option>
                </select>
              </div>

              {uploadResult?.key === d.Key && (
                <div className="callout callout-tip">
                  <span className="callout-label">Uploaded</span>
                  {uploadResult.matched.length
                    ? `Populating ${uploadResult.matched.length} field${uploadResult.matched.length === 1 ? '' : 's'}: ${uploadResult.matched.join(', ')}.`
                    : 'No recognised fields — nothing in this document would be populated.'}
                  {uploadResult.unrecognised.length > 0 && (
                    <> Not recognised, so left blank: {uploadResult.unrecognised.map((u) => u.tag).join(', ')}.</>
                  )}
                </div>
              )}

              <div className="tmpl-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => downloadTemplate(d.Key, d.FileName)}>
                  Download
                </button>
                <label className="btn btn-secondary btn-sm upload-btn">
                  {busy === d.Key ? 'Uploading…' : 'Replace'}
                  <input
                    type="file"
                    accept=".docx"
                    ref={(el) => { fileInputs.current[d.Key] = el; }}
                    onChange={(e) => upload(d.Key, e.target.files[0])}
                    disabled={busy === d.Key}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- Email templates ---------------- */}
      <div className="page-card">
        <h2 className="tmpl-section-heading">Email templates</h2>
        <p className="page-subtitle tmpl-section-sub">
          The message sent with each outcome. Edit the wording directly and use{' '}
          <strong>Insert field</strong> to drop in applicant details — those are filled in per
          applicant as each mail goes out. The Certificate of Good Standing signature is appended
          automatically unless you place <code>{'<img src="cid:fsa-signature">'}</code> yourself.
        </p>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Subject</th>
                <th className="nowrap">Last edited</th>
                <th className="nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((t) => (
                <tr key={t.Key}>
                  <td><strong>{t.Name}</strong></td>
                  <td className="tmpl-subject">{t.Subject}</td>
                  <td className="nowrap">
                    {formatDateTime(t.UpdatedAt)}
                    {t.UpdatedBy && <div className="tmpl-by">{t.UpdatedBy}</div>}
                  </td>
                  <td className="nowrap">
                    <button className="btn btn-ghost btn-sm" onClick={() => openEmail(t)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewing && (
        <div className="tmpl-viewer-backdrop" onClick={() => setViewing(null)}>
          <div className="tmpl-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="tmpl-viewer-head">
              <h3>{viewing.name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewing(null)}>Close</button>
            </div>
            <iframe src={`${viewing.url}#view=Fit`} title={viewing.name} className="tmpl-viewer-frame" />
          </div>
        </div>
      )}

      {editingEmail && (
        <Modal
          title={`Edit: ${editingEmail.Name}`}
          subtitle="Changes take effect on the next send. Use the application's Preview email button to check it."
          onClose={() => setEditingEmail(null)}
          wide
        >
          <div className="field">
            <label className="field-required" htmlFor="et-subject">Subject</label>
            <input
              id="et-subject"
              type="text"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
            />
          </div>

          <div className="field">
            <label className="field-required" htmlFor="et-body">Message</label>

            <div className="tmpl-toolbar">
              <button type="button" onClick={() => wrap('<strong>', '</strong>')} title="Bold">
                <strong>B</strong>
              </button>
              <button type="button" onClick={() => wrap('<em>', '</em>')} title="Italic">
                <em>I</em>
              </button>
              <button type="button" onClick={() => insert('<br>')} title="Line break">↵ Line</button>
              <button type="button" onClick={() => insert('<br><br>')} title="New paragraph">¶ Paragraph</button>
              <button type="button" onClick={() => insert('<br>&bull; ')} title="Bullet point">&bull; Bullet</button>
              <span className="tmpl-toolbar-sep" />
              <FieldPicker tokens={tokens} onPick={(t) => insert(`{{${t}}}`)} />
            </div>

            <textarea
              id="et-body"
              ref={bodyRef}
              className="tmpl-body-editor"
              value={emailForm.bodyHtml}
              onChange={(e) => setEmailForm({ ...emailForm, bodyHtml: e.target.value })}
            />
            <span className="field-hint">
              Buttons act on whatever you have selected. Anything in double braces is replaced
              per applicant when the mail goes out.
            </span>
          </div>

          {rendered?.unknown?.length > 0 && (
            <div className="callout callout-warning">
              <span className="callout-label">Not a known field</span>
              {rendered.unknown.map((u) => <code key={u} className="tmpl-bad-token">{`{{${u}}}`}</code>)}
              {' '}would reach the client exactly as written. Check the spelling against
              <strong> Insert field</strong>.
            </div>
          )}

          <div className="tmpl-live-preview">
            <span className="tmpl-controls-label">
              Preview
              <span className="tmpl-preview-note">shown with example values</span>
            </span>
            <div className="tmpl-live-preview-subject">
              <span>Subject</span>{rendered ? rendered.subject : emailForm.subject}
            </div>
            <div
              className="tmpl-live-preview-body"
              // Authored by Super Admins inside this app — trusted content.
              dangerouslySetInnerHTML={{ __html: rendered ? rendered.bodyHtml : emailForm.bodyHtml }}
            />
          </div>

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setEditingEmail(null)}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={saveEmail}
              disabled={busy === 'email' || !emailForm.subject.trim() || !emailForm.bodyHtml.trim()}
            >
              {busy === 'email' ? 'Saving…' : 'Save template'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Templates;
